import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { handleApiError, showSuccessToast } from '@/utils/errorHandling'
import {
  Plus,
  Search,
  Filter,
  Play,
  Pause,
  Users,
  Calendar,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  BarChart2,
  Swords
} from 'lucide-react'
import { apiClient } from '@/services/api'
import { useTerminology } from '@/hooks/useTerminology'
import type { Lesson } from '@/types'
import styles from './Lessons.module.css'

export function Lessons() {
  const navigate = useNavigate()
  const t = useTerminology()
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null)

  useEffect(() => {
    loadLessons()
  }, [statusFilter])

  // Matrix rain effect
  useEffect(() => {
    const matrixEffect = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')!
      if (!ctx) return

      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      canvas.style.position = 'fixed'
      canvas.style.top = '0'
      canvas.style.left = '0'
      canvas.style.zIndex = '0'
      canvas.style.pointerEvents = 'none'
      canvas.style.opacity = '0.1'

      document.body.appendChild(canvas)

      const matrix = '01001000011001010110110001110000'
      const matrixArray = matrix.split('')

      const fontSize = 14
      const columns = canvas.width / fontSize
      const drops = Array(Math.floor(columns)).fill(1)

      function draw() {
        ctx.fillStyle = 'rgba(0, 8, 17, 0.1)'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        ctx.fillStyle = '#00ff88'
        ctx.font = `${fontSize}px Roboto Mono, monospace`

        for (let i = 0; i < drops.length; i++) {
          const text = matrixArray[Math.floor(Math.random() * matrixArray.length)]
          ctx.fillText(text, i * fontSize, drops[i] * fontSize)

          if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
            drops[i] = 0
          }
          drops[i]++
        }
      }

      const interval = setInterval(draw, 100)

      return () => {
        clearInterval(interval)
        document.body.removeChild(canvas)
      }
    }

    const cleanup = matrixEffect()
    return cleanup
  }, [])

  const loadLessons = async () => {
    try {
      setLoading(true)
      setError(null) // Clear previous errors
      const response = await apiClient.getLessons({
        page: 1
      })
      setLessons(response.data || [])
    } catch (err: any) {
      const errorMessage = handleApiError(err, 'load lessons', false)
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (term: string) => {
    setSearchTerm(term)
    // Debounce search - you could add useDebounce hook here
    setTimeout(() => loadLessons(), 300)
  }

  const handleLessonAction = async (lessonId: string, action: 'start' | 'pause' | 'delete') => {
    try {
      switch (action) {
        case 'start':
          await apiClient.startLesson(lessonId)
          showSuccessToast('Lesson started successfully!')
          break
        case 'pause':
          await apiClient.pauseLesson(lessonId)
          showSuccessToast('Lesson paused successfully!')
          break
        case 'delete':
          if (confirm('Are you sure you want to delete this lesson?')) {
            await apiClient.deleteLesson(lessonId)
            showSuccessToast('Lesson deleted successfully!')
          } else {
            return
          }
          break
      }
      await loadLessons() // Refresh the list
    } catch (err: any) {
      handleApiError(err, `${action} lesson`)

      // Only set general error state for critical failures
      if (err.response?.status >= 500) {
        setError('Service temporarily unavailable. Please try again later.')
      }
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
      case 'running': return styles.statusLive
      case 'draft': return styles.statusDraft
      case 'paused': return styles.statusPaused
      case 'completed': return styles.statusCompleted
      default: return styles.statusDraft
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'live':
      case 'running': return <Play className="w-4 h-4" />
      case 'paused': return <Pause className="w-4 h-4" />
      default: return null
    }
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>{`LOADING ${t.lesson.upperPlural}`}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>{error}</p>
          <button
            onClick={loadLessons}
            className={styles.retryButton}
          >
            TRY AGAIN
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={styles.header}
      >
        <div className={styles.headerContent}>
          <div className={styles.titleSection}>
            <h1>{t.lesson.upperPlural}</h1>
            <p>Interactive Learning Session Management</p>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className={styles.createButton}
          >
            <Plus className="w-4 h-4" />
            <span>{`NEW ${t.lesson.upper}`}</span>
          </button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={styles.filtersCard}
      >
        <div className={styles.filtersGrid}>
          {/* Search */}
          <div className={styles.searchWrapper}>
            <Search className={styles.searchIcon} />
            <input
              type="text"
              placeholder={`Search ${t.lesson.lowerPlural}, templates, or pirates...`}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className={styles.searchInput}
              aria-label={`Search ${t.lesson.lowerPlural}`}
            />
          </div>

          {/* Status Filter */}
          <div className={styles.filterWrapper}>
            <Filter className={styles.filterIcon} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={styles.filterSelect}
              aria-label="Filter by status"
            >
              <option value="all">ALL STATUS</option>
              <option value="draft">DRAFT</option>
              <option value="live">LIVE</option>
              <option value="paused">PAUSED</option>
              <option value="completed">COMPLETED</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Lessons Grid */}
      {lessons.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={styles.emptyContainer}
        >
          <div className={styles.emptyIcon}>
            <Swords className="w-12 h-12" />
          </div>
          <h3 className={styles.emptyTitle}>{`NO ${t.lesson.upperPlural} FOUND`}</h3>
          <p className={styles.emptyText}>
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : `Get started by creating your first ${t.lesson.lower}`
            }
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className={styles.emptyButton}
            >
              <Plus className="w-4 h-4" />
              <span>{`CREATE ${t.lesson.upper}`}</span>
            </button>
          )}
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={styles.lessonsGrid}
        >
          {lessons.map((lesson, index) => (
            <motion.div
              key={lesson.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className={styles.lessonCard}
              style={{ zIndex: selectedLesson?.id === lesson.id ? 100 : 1 }}
            >
              <div className={styles.cardContent}>
                {/* Lesson Header */}
                <div className={styles.cardHeader}>
                  <div className={styles.cardInfo}>
                    <h3 className={styles.lessonName}>
                      {lesson.name}
                    </h3>
                    <p className={styles.lessonDescription}>
                      {(lesson as any).description || 'No description provided'}
                    </p>
                  </div>

                  <div className={styles.cardActions}>
                    <span className={`${styles.statusPill} ${getStatusColor(lesson.status)}`}>
                      {getStatusIcon(lesson.status)}
                      <span>{lesson.status}</span>
                    </span>

                    {/* Actions Menu */}
                    <div className="relative">
                      <button
                        onClick={() => setSelectedLesson(selectedLesson?.id === lesson.id ? null : lesson)}
                        className={styles.menuButton}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {selectedLesson?.id === lesson.id && (
                        <div className={styles.dropdownMenu}>
                          <button
                            onClick={() => {/* Navigate to lesson details */}}
                            className={styles.dropdownItem}
                          >
                            <Eye className="w-4 h-4" />
                            VIEW DETAILS
                          </button>
                          <button
                            onClick={() => {
                              setSelectedLesson(null)
                              navigate(`/lessons/${lesson.id}/edit`)
                            }}
                            className={styles.dropdownItem}
                          >
                            <Edit className="w-4 h-4" />
                            {`EDIT ${t.lesson.upper}`}
                          </button>
                          {lesson.status === 'draft' && (
                            <button
                              onClick={() => handleLessonAction(lesson.id, 'start')}
                              className={styles.dropdownItem}
                            >
                              <Play className="w-4 h-4" />
                              {`START ${t.lesson.upper}`}
                            </button>
                          )}
                          {lesson.status === 'live' && (
                            <>
                              <button
                                onClick={() => navigate(`/lesson/${lesson.id}?host=true`)}
                                className={styles.dropdownItem}
                              >
                                <Eye className="w-4 h-4" />
                                VIEW LIVE
                              </button>
                              <button
                                onClick={() => handleLessonAction(lesson.id, 'pause')}
                                className={styles.dropdownItem}
                              >
                                <Pause className="w-4 h-4" />
                                {`PAUSE ${t.lesson.upper}`}
                              </button>
                            </>
                          )}
                          {(lesson.status === 'completed' || lesson.status === 'cancelled') && (
                            <button
                              onClick={() => {
                                setSelectedLesson(null)
                                navigate(`/lessons/${lesson.id}/report`)
                              }}
                              className={styles.dropdownItem}
                            >
                              <BarChart2 className="w-4 h-4" />
                              VIEW REPORT
                            </button>
                          )}
                          <div className={styles.dropdownDivider} />
                          <button
                            onClick={() => handleLessonAction(lesson.id, 'delete')}
                            className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                          >
                            <Trash2 className="w-4 h-4" />
                            {`DELETE ${t.lesson.upper}`}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Lesson Stats */}
                <div className={styles.lessonStats}>
                  <div className={styles.statItem}>
                    <Users className="w-4 h-4" />
                    <span>{lesson.player_count || 0} students</span>
                  </div>
                  <div className={styles.statItem}>
                    <Calendar className="w-4 h-4" />
                    <span>{new Date((lesson as any).created_at || new Date()).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Progress Bar (if applicable) */}
                {(lesson.status === 'live' || lesson.status === 'running') && (lesson as any).progress !== undefined && (
                  <div className={styles.progressSection}>
                    <div className={styles.progressHeader}>
                      <span className={styles.progressLabel}>{`${t.lesson.title} Progress`}</span>
                      <span className={styles.progressLabel}>{Math.round((lesson as any).progress)}%</span>
                    </div>
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${(lesson as any).progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Create Lesson Modal */}
      {showCreateModal && (
        <CreateLessonModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            loadLessons()
          }}
        />
      )}
    </div>
  )
}

// Simple Create Lesson Modal Component
function CreateLessonModal({
  onClose,
  onSuccess
}: {
  onClose: () => void
  onSuccess: () => void
}) {
  const t = useTerminology()
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Structure the data to match Rails parameter expectations
      const lessonData = {
        lesson: {
          name: formData.name
        }
      }

      await apiClient.createLesson(lessonData)
      showSuccessToast(`${t.lesson.title} created successfully!`)
      onSuccess()
    } catch (err: any) {
      handleApiError(err, 'create lesson')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="create-lesson-title">
      <div className={styles.modalContent}>
        <h2 id="create-lesson-title" className={styles.modalTitle}>{`CREATE NEW ${t.lesson.upper}`}</h2>

        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              {`${t.lesson.title} Name`}
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={styles.formInput}
              placeholder="Enter pirate mission name..."
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Description
            </label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={styles.formTextarea}
              placeholder="Describe your learning mission..."
            />
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={`${styles.modalButton} ${styles.cancelButton}`}
            >
              CANCEL
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`${styles.modalButton} ${styles.submitButton}`}
            >
              {loading ? 'CREATING...' : `CREATE ${t.lesson.upper}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
